import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import * as manufacturingApi from '../../lib/api/manufacturing';

// "완료/종료"로 보는 상태 - 이 목록에 없는 상태는 전부 "진행 중"으로 취급한다.
// 2026-10-11: 애초에 MANU_PROCESS_INF(제조 공정 단계별 실행 기록) 기반으로
// 만들었다가, 그 테이블이 로컬/운영 모두 비어 있고(쓰이지 않는 레거시 경로) 공통코드
// 조인도 이 화면이 가정한 평평한 GRP_COD/COD 구조가 아니라 실제론 계층형
// (BLC_CD/MLC_CD/SLC_CD) 이라 바로 깨졌다. 생산계획(MANU_PLAN_INF) 자체에
// PRGS_RT(진행률)/담당자(MGR_USR_NM 등)/일정이 이미 다 있고, 프로젝트별 제조
// 화면(ManufacturingListContainer, getProjectsWithSummary)이 쓰는 것과 같은
// GET /api/v1/manufacturing/plans 를 그대로 재사용한다. statusName 은 서버가
// COM_COMMON_INF(BLC_CD='MANU', MLC_CD='PLAN_STATUS') 계층형 코드를 이미
// 조인해서 내려주므로(ManufacturingPlanMapper.xml) 프론트에서 다시 풀지 않는다.
const DONE_STATUSES = new Set(['CMPL', 'COMPLETED', 'SHIPPED', 'CANCELLED', 'CNCL', 'CLOSED']);

/**
 * "생산 현황" 의 공정 진행 탭.
 *
 * 신규 백엔드 엔드포인트를 만들지 않고, 프로젝트별 제조 화면이 이미 쓰는
 * GET /api/v1/manufacturing/plans (생산계획 목록) 를 조회 전용으로 재사용한다.
 * 완료/출하/취소/종결 상태는 빼고, 계획 종료일(마감일)이 빠른 순으로 보여준다.
 */
const ProcessProgressView = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await manufacturingApi.getManufacturingPlans({
          page: 1,
          size: 100,
        });
        const content = response?.data?.data?.content || [];
        if (!cancelled) {
          setRows(content);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || '공정 진행 현황을 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // 완료/출하/취소/종결 건은 "진행 중" 요약에서 뺀다. 계획 종료일(마감일) 빠른 순.
  const inProgress = useMemo(() => {
    return rows
      .filter((r) => !DONE_STATUSES.has(r.status))
      .sort((a, b) => (a.planEndDate || '9999-99-99').localeCompare(b.planEndDate || '9999-99-99'));
  }, [rows]);

  const today = dayjs().format('YYYY-MM-DD');

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        진행 중인 생산계획을 마감일이 빠른 순으로 보여줍니다(조회 전용). 마감일이 지난
        행은 빨갛게 표시됩니다. 상세 처리는 프로젝트별 제조 화면에서 합니다.
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>생산계획 번호</TableCell>
              <TableCell>프로젝트 / 제품</TableCell>
              <TableCell>현재 상태</TableCell>
              <TableCell>담당자</TableCell>
              <TableCell>계획 기간</TableCell>
              <TableCell>진행률</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {inProgress.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  진행 중인 생산계획이 없습니다.
                </TableCell>
              </TableRow>
            )}
            {inProgress.map((row) => {
              const overdue = Boolean(row.planEndDate) && row.planEndDate < today;
              return (
                <TableRow
                  key={row.planId}
                  hover
                  sx={overdue ? { backgroundColor: 'rgba(211, 47, 47, 0.08)' } : undefined}
                >
                  <TableCell>{row.planNumber || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.projectName || '-'}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.productName || ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={row.statusName || row.status || '-'} variant="outlined" />
                  </TableCell>
                  <TableCell>{row.managerName || '-'}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={overdue ? 'error.main' : 'text.primary'}
                      fontWeight={overdue ? 'bold' : 'normal'}
                    >
                      {row.planStartDate || '-'} ~ {row.planEndDate || '-'}
                      {overdue ? ' (지연)' : ''}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={Number(row.progressRate) || 0}
                        sx={{ flexGrow: 1, height: 6, borderRadius: 1 }}
                      />
                      <Typography variant="caption">{Number(row.progressRate) || 0}%</Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProcessProgressView;
